import {
  UPLOAD_TAX_ENTRIES_TO_IMPORT,
  UPLOAD_TAX_ENTRIES_TO_IMPORT_SUCCESS,
  FETCH_TAX_ENTRY_TO_IMPORT_DATA_SUCCESS,
  SUBMIT_BATCH_TAX_ENTRIES,
  SUBMIT_BATCH_TAX_ENTRIES_SUCCESS,
  SUBMIT_BATCH_TAX_ENTRIES_FAILURE,
  RESET_DATA_FOR_BATCH_TAX_ENTRIES,
  UPLOAD_TAX_ENTRIES_TO_IMPORT_FAILED,
} from './types';
import { parseAmount } from './utils';
import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';

import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';
import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';

import { normalizeCellToString } from '../../../utils/readXlsxFile.utils';

import { fetchingError, showAlert, hideAlert } from '../../../shared/store/actions';
import { countryTaxTypesByDescriptionSelector } from '../../../shared/store/selectors';
import {
  errorNotification,
  successNotification,
  infoNotification,
  warningNotification,
} from '../../../shared/store/notificationActions';
import { fetchProposedJournals, resetProposedJournals } from '../../store/actions';
import {
  submitBatchTaxEntriesForApproval as submitBatchTaxEntriesForApprovalApi,
  validateAndProcessTaxEntries as validateAndProcessTaxEntriesApi,
} from '../api';
import {
  addAttachments as addAttachmentsApi,
} from '../../../shared/api';
import {
  sortErrors,
} from '../errors';

import alertTypes from '../../../shared/alertTypes';

const placeEntriesWithErrorFirst = entries => entries.reduce((acc, element) => {
  if (element.validationError) {
    return [element, ...acc];
  }
  return [...acc, element];
}, []);

export const resetDataForBatchTaxEntries = () => ({
  type: RESET_DATA_FOR_BATCH_TAX_ENTRIES,
});

const validateTaxEntry = entry => {
  const clientSideErrors = [];

  const { fromAccount, fromTaxYear } = entry;


  if (entry.entryActivity === 'RECLASS' && !fromAccount) {
    clientSideErrors.push({
      error: 'Deferred Reclass From Account is required on the originating Reclass Activity record',
      type: 'client.reclassAccountRequired',
    });
  }

  if (entry.entryActivity !== 'RECLASS' && fromAccount) {
    clientSideErrors.push({
      error: 'Deferred Reclass From Account is not allowed for the activity',
      type: 'client.reclassAccountNotAllowed',
    });
  }

  if (entry.entryActivity === 'RECLASS' && !fromTaxYear) {
    clientSideErrors.push({
      error: 'Deferred Reclass From Tax Year is required on the originating Reclass Activity record',
      type: 'client.reclassTaxYearRequired',
    });
  }

  if (entry.entryActivity !== 'RECLASS' && fromTaxYear) {
    clientSideErrors.push({
      error: 'Deferred Reclass From Tax Year is not allowed for the activity',
      type: 'client.reclassTaxYearNotAllowed',
    });
  }

  return clientSideErrors;
};


const preprocessTaxEntry = (acc, entry) => {
  const { fromAccount, fromTaxYear, ...entryToInsert } = entry;

  const entryErrors = validateTaxEntry(entry);

  if (entry.entryActivity !== 'RECLASS') {
    return {
      ...acc,
      taxEntries: [...acc.taxEntries, entryToInsert],
      clientSideErrors: [...acc.clientSideErrors, entryErrors],
    };
  }

  return {
    taxEntries: [
      ...acc.taxEntries,
      entryToInsert,
      {
        ...entryToInsert,
        account: fromAccount,
        taxYear: fromTaxYear,
        netAmount: entry.netAmount ? entry.netAmount * -1 : entry.netAmount,
        grossAmount: entry.grossAmount ? entry.grossAmount * -1 : entry.grossAmount,
      },
    ],
    clientSideErrors: [...acc.clientSideErrors, entryErrors, entryErrors],
  };
};


const TaxEntryFieldMappings = {
  topside: 'topsideFlag',
  company: 'companyCode',
  taxyear: 'taxYear',
  countrytaxtype: 'countryTaxTypeDescription',
  entrytype: 'entryType',
  activity: 'entryActivity',
  booktype: 'bookType',
  payablegroup: 'payableTag',
  account: 'account',
  netamount: 'netAmount',
  grossamount: 'grossAmount',
  deferredreclassfromaccount: 'fromAccount',
  deferredreclassfromtaxyear: 'fromTaxYear',
  glaccountoffset: 'glAccountOverride',
  generatematchingstateandfbosentries: 'fbosFlag',
  comment: 'comment',
};

const TaxEntryNormalizers = {
  taxyear: normalizeCellToString,
  account: normalizeCellToString,
  payablegroup: normalizeCellToString,
  deferredreclassfromaccount: normalizeCellToString,
  deferredreclassfromtaxyear: normalizeCellToString,
  glaccountoffset: normalizeCellToString,
  company: normalizeCellToString,
  comment: normalizeCellToString,
};


const readBatchTaxEntriesXlsxFileAsJson = file => {
  const columnHeaders = Object.keys(TaxEntryFieldMappings);
  return readXlsxFileAsJson({
    file,
    fileValidators: [excelFileTypeValidator],
    prevalidators: [columnHeadersValidator],
    postvalidators: [noEmptyRowsValidator],
    fieldMappings: TaxEntryFieldMappings,
    getRange: calculateImportRangeFactory('P'),
    preprocessors: [emptyRowsTrimmer],
    mapToJsonOptions: {
      header: columnHeaders,
    },
    validationData: {
      columnHeaders,
      minColumnsNumber: 16,
      maxColumnsNumber: 16,
    },
    fieldNormalizers: TaxEntryNormalizers,
  });
};

export const uploadTaxEntriesToImport = (file, currentPeriod) => async dispatch => {
  dispatch({
    type: UPLOAD_TAX_ENTRIES_TO_IMPORT,
  });
  dispatch(hideAlert());
  try {
    dispatch(resetProposedJournals());
    const rawTaxEntries = await readBatchTaxEntriesXlsxFileAsJson(file);

    const {
      clientSideErrors,
      taxEntries,
    } = rawTaxEntries.reduce(preprocessTaxEntry, { clientSideErrors: [], taxEntries: [] });

    const { entries, errors } = await validateAndProcessTaxEntriesApi({
      periodName: currentPeriod.name,
      entries: taxEntries.map(({ countryTaxTypeDescription, ...entry }) => ({
        ...entry,
        periodName: currentPeriod.name,
        countryTaxTypeDescription,
        netAmount: parseAmount(entry.netAmount),
        grossAmount: parseAmount(entry.grossAmount),
      })),
    });

    const serverSideErrors = errors || {};

    const taxEntriesWithValidation = entries.map((taxEntry, index) => ({
      validationError: sortErrors([
        ...(serverSideErrors[index] || []),
        ...(clientSideErrors[index] || []),
      ])
        .map(error => error?.error || '')
        .filter(Boolean)
        .join(', '),
      ...taxEntry,
    }));

    return dispatch({
      type: UPLOAD_TAX_ENTRIES_TO_IMPORT_SUCCESS,
      taxEntriesToImport: placeEntriesWithErrorFirst(taxEntriesWithValidation),
    });
  } catch (error) {
    dispatch({
      type: UPLOAD_TAX_ENTRIES_TO_IMPORT_FAILED,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    dispatch(showAlert(error.message, alertTypes.DANGER));
    return;
  }
};

export const fetchTaxEntryToImportData = (chosenTaxEntry, currentPeriod) => async (dispatch, getState) => {
  const fetchTaxEntryToImportDataSuccess = chosenTaxEntry => ({
    type: FETCH_TAX_ENTRY_TO_IMPORT_DATA_SUCCESS,
    chosenTaxEntry,
  });

  const ctts = countryTaxTypesByDescriptionSelector(getState());

  if (!chosenTaxEntry.validationError) {
    await dispatch(fetchProposedJournals({
      amount: chosenTaxEntry.netAmount,
      bookType: chosenTaxEntry.bookType,
      companyCode: chosenTaxEntry.companyCode,
      countryTaxType: chosenTaxEntry.countryTaxType
        // countryTaxType is not present if validation fails
        || ctts[chosenTaxEntry.countryTaxTypeDescription],
      currency: chosenTaxEntry.currency,
      entryActivity: chosenTaxEntry.entryActivity,
      entryType: chosenTaxEntry.entryType,
      fromAccount: chosenTaxEntry.manualFromAdjustment || chosenTaxEntry.fromAccount,
      glAccountOverride: chosenTaxEntry.glAccountOverride,
      period: currentPeriod,
      account: (
        chosenTaxEntry.account
        || chosenTaxEntry.manualAdjustment
        || chosenTaxEntry.manualToAdjustment
        || chosenTaxEntry.teAdjId
      ),
      taxYear: chosenTaxEntry.taxYear,
      topsideFlag: chosenTaxEntry.topsideFlag,
    }));
  } else {
    dispatch(resetProposedJournals());
  }

  return dispatch(fetchTaxEntryToImportDataSuccess(chosenTaxEntry));
};

export const submitBatchTaxEntriesForApproval = ({
  taxEntriesToImport,
  currentPeriod,
  files,
}, callback) => async dispatch => {
  const submitBatchTaxEntriesSuccess = ({ insertedTaxEntries }) => ({
    type: SUBMIT_BATCH_TAX_ENTRIES_SUCCESS,
    insertedTaxEntries,
  });

  try {
    dispatch({ type: SUBMIT_BATCH_TAX_ENTRIES });
    dispatch(infoNotification('Saving attachments'));

    const attachments = await addAttachmentsApi(files);
    const mappedFiles = files.map((file, index) => ({
      ...file,
      attachmentId: attachments[index].attachmentId,
    }));

    const { insertedTaxEntries } = await submitBatchTaxEntriesForApprovalApi({
      periodName: currentPeriod.name,
      taxEntries: taxEntriesToImport,
      files: mappedFiles,
      source: 'Batch',
    });

    if (callback) {
      callback();
    }

    dispatch(successNotification(`${insertedTaxEntries} Records saved`));
    return dispatch(submitBatchTaxEntriesSuccess({
      insertedTaxEntries,
    }));
  } catch (error) {
    dispatch({ type: SUBMIT_BATCH_TAX_ENTRIES_FAILURE });

    if (error.isTimeoutError) {
      dispatch(fetchingError());
      dispatch(warningNotification('Request timed out. Tax entries will be uploaded in the background.'));
      dispatch(resetDataForBatchTaxEntries());
      return;
    }

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

